/**
 * Subscription / billing service.
 *
 * Tenant-facing view of plans + current subscription + invoices +
 * outbound webhooks. Not to be confused with the operator-facing
 * /v1/operator/plans + Subscriptions surfaces — tenants see only
 * their own row (resolved via X-Organization-Id).
 *
 * The full S2.B + S2.C surface lives here; the /system/billing UI
 * and any other consumers go through this typed client rather than
 * hand-rolled fetch calls.
 */
import { http } from './client';
import type { Schema } from './types';

export type Plan = Schema<'PlanResponse'>;
export type Subscription = Schema<'SubscriptionResponse'>;
export type Usage = Schema<'UsageResponse'>;
export type Invoice = Schema<'InvoiceResponse'>;
export type TenantWebhook = Schema<'TenantWebhookResponse'>;
export type CheckoutResponse = Schema<'CheckoutResponse'>;

export type SubscriptionStatus =
  | 'trialing' | 'active' | 'past_due' | 'suspended' | 'cancelled';

export type ChangePlanAction =
  | 'cancel_workers_now'
  | 'schedule_for_period_end';

/** Global catalog of plans available to every tenant. */
export const plansApi = {
  list: () => http.get<Plan[]>('/v1/subscription/plans'),
};

/** The caller's current subscription row (1:1 with the org). */
export const currentApi = {
  get: () => http.get<Subscription>('/v1/subscription/current'),
};

export const usageApi = {
  get: () => http.get<Usage>('/v1/subscription/usage'),
};

export const checkoutApi = {
  /** Returns ``{checkout_url}`` to redirect the browser to. */
  create: (body: {
    plan_id: string;
    provider_code?: string;
    return_url: string;
    cancel_url: string;
    customer_email?: string;
    /** Billing cadence. Omitted ⇒ the backend defaults to 'month'. */
    billing_interval?: 'month' | 'year';
  }) => http.post<CheckoutResponse>('/v1/subscription/checkout', {
    provider_code: 'stripe',
    ...body,
  }),
};

export const billingPortalApi = {
  /**
   * Start a provider-hosted session for changing the card on file.
   *
   * `portal_url` is null for every organization that has no provider customer
   * yet — every Free-plan tenant and every trial before card entry — and when
   * the provider is unreachable. Callers must treat null as "no destination",
   * never as an error to surface as a failure: a billing page that looks broken
   * is the worst possible moment to ask someone for a card number.
   */
  create: (return_url: string) =>
    http.post<{ portal_url: string | null }>(
      '/v1/subscription/billing-portal', { return_url }),
};

export interface PaymentMethodCard {
  /** The provider's own brand token: `visa`, `mastercard`, `amex`, `unionpay`… */
  brand: string | null;
  last4: string;
  exp_month: number | null;
  exp_year: number | null;
}

export const paymentMethodApi = {
  /**
   * Which card the subscription will actually be charged.
   *
   * `card: null` is a NORMAL answer, not an error: every Free-plan org, every trial
   * before card entry, a provider without the lookup, and a provider that could not be
   * reached. The billing page renders the block without the detail — the same posture
   * `billingPortalApi` takes on a null url, and for the same reason.
   *
   * Its own request rather than a field on the subscription response, deliberately:
   * answering it costs a round trip to the payment provider, and the subscription
   * response is read by the TopNav upgrade dialog, the dashboard and every plan gate.
   * Only the billing page asks, so only the billing page pays the latency.
   */
  get: () =>
    http.get<{ card: PaymentMethodCard | null }>('/v1/subscription/payment-method'),
};

export const subscriptionMutationsApi = {
  cancel: (at_period_end: boolean = true) =>
    http.post<Subscription>('/v1/subscription/cancel', { at_period_end }),

  resume: () => http.post<Subscription>('/v1/subscription/resume'),

  /** Change plan. First call without `action` returns 409 with
   *  structured choices on over-limit. Subsequent call provides the
   *  chosen action + (when cancel_workers_now) worker_ids. */
  changePlan: (body: {
    plan_id: string;
    action?: ChangePlanAction;
    worker_ids_to_deactivate?: string[];
  }) => http.post<Subscription>('/v1/subscription/change-plan', body),
};

export const invoicesApi = {
  list: (filters?: {
    status?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.start_date) qs.set('start_date', filters.start_date);
    if (filters?.end_date) qs.set('end_date', filters.end_date);
    if (filters?.limit) qs.set('limit', String(filters.limit));
    const suffix = qs.toString() ? `?${qs}` : '';
    return http.get<Invoice[]>(`/v1/subscription/invoices${suffix}`);
  },

  get: (id: string) => http.get<Invoice>(`/v1/subscription/invoices/${id}`),

  /**
   * Fetch the invoice CSV as a Blob, for the caller to save.
   *
   * WHY THIS RETURNS A BLOB AND NOT A URL (2026-07-31)
   * --------------------------------------------------
   * It used to be `exportCsvUrl(): string`, and the Billing page did
   * `window.location.href = url`. Three things were wrong with that, and the
   * user-visible result was that Export CSV navigated to /dashboard:
   *
   *   1. The path is RELATIVE, so the browser navigated to the SPA's own origin
   *      (qutime.com/v1/...) rather than the API host. CloudFront answers unknown
   *      paths with index.html, React Router then matched nothing, and the
   *      catch-all `<RoleRedirect />` sent the user to their landing page.
   *   2. A browser navigation carries no `Authorization` header. This SPA
   *      authenticates with a bearer token from localStorage, not a cookie — the
   *      old comment claiming it "reuses the existing session cookie" was simply
   *      wrong about how this app authenticates.
   *   3. It bypassed the axios client, so no baseURL, no org header, and no
   *      401-refresh retry.
   *
   * Returning a Blob makes all three impossible rather than merely detected: a
   * Blob cannot be assigned to `window.location.href`, and going through
   * `http.get` picks up the base URL, the auth header and the refresh interceptor
   * for free. The old docstring already said "via fetch + blob" — the
   * implementation just never did it.
   */
  exportCsv: (filters?: {
    status?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<Blob> => {
    const qs = new URLSearchParams();
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.start_date) qs.set('start_date', filters.start_date);
    if (filters?.end_date) qs.set('end_date', filters.end_date);
    const suffix = qs.toString() ? `?${qs}` : '';
    return http.get<Blob>(
      `/v1/subscription/invoices/export.csv${suffix}`,
      { responseType: 'blob' },
    );
  },
};

export const tenantWebhooksApi = {
  list: () => http.get<TenantWebhook[]>('/v1/subscription/webhooks'),

  create: (body: {
    name: string;
    url: string;
    event_subscriptions: string[];
  }) => http.post<TenantWebhook>('/v1/subscription/webhooks', body),

  update: (id: string, body: {
    name: string;
    url: string;
    event_subscriptions: string[];
  }) => http.patch<TenantWebhook>(`/v1/subscription/webhooks/${id}`, body),

  delete: (id: string) =>
    http.delete<void>(`/v1/subscription/webhooks/${id}`),

  test: (id: string) =>
    http.post<{ success: boolean; webhook_id: string }>(
      `/v1/subscription/webhooks/${id}/test`,
    ),
};

export const billingEventsApi = {
  list: (limit = 50) =>
    http.get<{ id: string; event_type: string; provider_event_id: string | null; created_at: string | null }[]>(
      `/v1/subscription/billing-events?limit=${limit}`,
    ),
};
